import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgxCurrencyDirective } from 'ngx-currency';

import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { DescontoMaximoPerfilComponent } from '../../../../shared/components/desconto-maximo-perfil/desconto-maximo-perfil.component';
import { PerfilOptionStruct } from '../../../../shared/services/structs/usuario/perfil-option.struct';

import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { TipoComposicaoCodigoBarrasEnum } from '../../../../shared/enums/vendas/tipo-composicao-codigo-barras.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';

import { ConfigPdvService } from '../../../../shared/services/apis/vendas/config-pdv.service';
import { ConfigPdvRequest } from '../../../../shared/services/requests/vendas/config-pdv.request';
import { DescontoPerfilStruct } from '../../../../shared/services/structs/vendas/desconto-perfil.struct';
import { EtapaVenda } from '../../../../shared/services/models/vendas/etapa-venda.model';
import { TipoImpressaoPdv } from '../../../../shared/services/models/vendas/tipo-impressao-pdv.model';
import { TipoComposicaoCodigoBarras } from '../../../../shared/services/models/vendas/tipo-composicao-codigo-barras.model';
import { TagVendaService } from '../../../../shared/services/apis/vendas/tag-venda.service';
import { TagVenda } from '../../../../shared/services/models/vendas/tag-venda.model';
import { LookupService } from '../../../../shared/services/apis/produto/produto-lookup.service';
import { LookupItemStruct as PessoaLookupItemStruct } from '../../../../shared/services/structs/pessoa/lookup-item.struct';
import { PerfilService } from '../../../../shared/services/apis/usuario/perfil.service';
import { SelectPesquisavelComponent } from '../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { MotorFiscalLookupService } from '../../../../shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { MaskService } from '../../../../shared/services/mask.service';
import { environment } from '../../../../../environments/environment';
import { TipoOperacaoEnum } from '../../../../shared/enums/motor-fiscal/tipo-operacao.enum';
import { LookupItemStruct } from '../../../../shared/services/structs/motor-fiscal/lookup-item.struct';

@Component({
  selector: 'app-config-pdv',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatIconModule,
    MatButtonModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    DescontoMaximoPerfilComponent,
    SelectPesquisavelComponent,
    NgxCurrencyDirective,
  ],
  templateUrl: './config-pdv.component.html',
  styleUrls: ['./config-pdv.component.css'],
})
export class ConfigPdvComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public form: FormGroup;
  public isLoading = false;
  public isSaving = false;

  private idConfigPdv = 0;
  public descontoPerfil: DescontoPerfilStruct[] = [];

  // Auxiliares para o skeleton loader (repetição de linhas de toggle por seção).
  public readonly skeletonTogglesSecao2 = Array.from({ length: 6 });
  public readonly skeletonTogglesSecao3 = Array.from({ length: 5 });

  // Lookups
  public opcoesEtapaVenda: EtapaVenda[] = [];
  public opcoesTags: TagVenda[] = [];
  public opcoesListaPreco: PessoaLookupItemStruct[] = [];
  public naturezaOperacaoPreload: LookupItemStruct[] = [];
  public readonly naturezaOperacaoApiUrl = environment.urlApiMotorFiscal + 'Lookup/natureza-operacao';
  public readonly naturezaOperacaoExtraParams = { idTipoOperacao: TipoOperacaoEnum.Saida };
  public opcoesLocalizacao: PessoaLookupItemStruct[] = [];
  public opcoesTipoImpressao: TipoImpressaoPdv[] = [];
  public opcoesTipoComposicao: TipoComposicaoCodigoBarras[] = [];
  public opcoesPerfis: PerfilOptionStruct[] = [];
  public percentMask: any;

  // FormControls expostos para o app-select-pesquisavel (modo offline)
  public get tagsControl(): FormControl {
    return this.form.get('listIdTagVenda') as FormControl;
  }
  public get listaPrecoControl(): FormControl {
    return this.form.get('idListaPrecoPreferencial') as FormControl;
  }
  public get localizacaoControl(): FormControl {
    return this.form.get('idLocalizacaoEstoquePadrao') as FormControl;
  }
  public get naturezaOperacaoControl(): FormControl {
    return this.form.get('idNaturezaOperacaoPadrao') as FormControl;
  }

  // Filhos do toggle mestre da Seção 3
  private readonly camposCaixaFilhos = [
    'considerarRecebimentosFaturarPrevenda',
    'permitirCompartilhamentoCaixa',
    'exibirValoresFormasRecebimento',
    'aberturaCaixaUltimoValor',
  ];

  constructor(
    private formBuilder: FormBuilder,
    private configPdvService: ConfigPdvService,
    private tagVendaService: TagVendaService,
    private lookupService: LookupService,
    private perfilService: PerfilService,
    private motorFiscalLookupService: MotorFiscalLookupService,
    private maskService: MaskService,
    private alertService: AlertService,
    private router: Router
  ) {
    this.percentMask = this.maskService.getPercentMask();
    this.form = this.formBuilder.group({
      // Seção 1
      idEtapaKanbanPadraoVenda: [null],
      listIdTagVenda: [[]],
      idListaPrecoPreferencial: [null],
      idNaturezaOperacaoPadrao: [null],
      bloquearVendaSemEstoque: [false],
      idLocalizacaoEstoquePadrao: [null],
      // Seção 2
      vendedorObrigatorio: [false],
      bloquearVendaValorInferior: [false],
      permitirVendaProdutoNaoCadastrado: [false],
      bloquearAlteracaoPreco: [false],
      agrupamentoAutomatico: [false],
      mostrarTelaRecebimento: [false],
      isEmissaoAutomaticaNFCe: [false],
      listIdTipoImpressao: [[]],
      descontoMaximoItens: [10, [Validators.required, Validators.min(0), Validators.max(100)]],
      // Seção 3
      controlarCaixa: [true],
      considerarRecebimentosFaturarPrevenda: [false],
      permitirCompartilhamentoCaixa: [false],
      exibirValoresFormasRecebimento: [false],
      aberturaCaixaUltimoValor: [false],
      // Seção 4
      idTipoComposicaoCodigoBarras: [TipoComposicaoCodigoBarrasEnum.PorPreco],
      // Seção 5
      permitirAutoAtendimento: [false],
      listIdPerfilAutoatendimento: [[]],
    });
  }

  ngOnInit(): void {
    this.setupListeners();
    this.carregarDados();
  }

  private setupListeners(): void {
    this.form.get('controlarCaixa')?.valueChanges.subscribe((ativo: boolean) => {
      this.aplicarEstadoCaixa(ativo);
    });
    this.form.get('permitirAutoAtendimento')?.valueChanges.subscribe((ativo: boolean) => {
      this.aplicarEstadoAutoAtendimento(ativo);
    });
  }

  private aplicarEstadoCaixa(ativo: boolean): void {
    this.camposCaixaFilhos.forEach(campo => {
      const control = this.form.get(campo);
      if (ativo) {
        control?.enable({ emitEvent: false });
      } else {
        // Ao desativar o controle de caixa, os toggles filhos são zerados (false) e desabilitados.
        control?.setValue(false, { emitEvent: false });
        control?.disable({ emitEvent: false });
      }
    });
  }

  private aplicarEstadoAutoAtendimento(ativo: boolean): void {
    const control = this.form.get('listIdPerfilAutoatendimento');
    if (ativo) {
      control?.enable({ emitEvent: false });
    } else {
      control?.disable({ emitEvent: false });
    }
  }

  private carregarDados(): void {
    this.isLoading = true;
    forkJoin({
      config: this.configPdvService.get().pipe(catchError(() => of(null))),
      lookups: this.configPdvService.getLookups().pipe(catchError(() => of(null))),
      tags: this.tagVendaService.getAll('', 0, 1000).pipe(catchError(() => of(null))),
      listaPreco: this.lookupService.getListaPrecoSelect().pipe(catchError(() => of(null))),
      localizacao: this.lookupService.getLocalizacaoEstoqueSelect().pipe(catchError(() => of(null))),
      perfis: this.perfilService.getAllLookup().pipe(catchError(() => of(null))),
      natureza: this.motorFiscalLookupService.getNaturezaOperacaoLookup(TipoOperacaoEnum.Saida),
    }).subscribe({
      next: res => {
        this.isLoading = false;

        if (res.lookups && !res.lookups.isError) {
          this.opcoesEtapaVenda = res.lookups.listEtapaVenda || [];
          this.opcoesTipoImpressao = res.lookups.listTipoImpressao || [];
          this.opcoesTipoComposicao = res.lookups.listTipoComposicaoCodigoBarras || [];
        }
        if (res.tags && !res.tags.isError) {
          this.opcoesTags = res.tags.listaTags || [];
        }
        if (res.listaPreco && !res.listaPreco.isError) {
          this.opcoesListaPreco = res.listaPreco.list || [];
        }
        if (res.localizacao && !res.localizacao.isError) {
          this.opcoesLocalizacao = res.localizacao.list || [];
        }
        if (res.perfis && !res.perfis.isError) {
          this.opcoesPerfis = (res.perfis.listaPerfil || []).map(p => ({
            idPerfil: p.perfil.idPerfil,
            nome: p.perfil.nome,
          }));
        } else if (res.perfis?.isError) {
          this.alertService.show('Aviso', res.perfis.errorDescription, AlertType.warning);
        }
        this.naturezaOperacaoPreload = res.natureza || [];

        if (res.config && !res.config.isError && res.config.configPdv) {
          this.preencherFormulario(res.config);
        } else if (res.config?.isError) {
          this.alertService.show('Aviso', res.config.errorDescription, AlertType.warning);
        }

        this.resetScrollToTop();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
        this.resetScrollToTop();
      },
    });
  }

  private resetScrollToTop(): void {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }

  private preencherFormulario(config: {
    configPdv: any;
    listIdTagVenda: number[];
    listDescontoPerfil: DescontoPerfilStruct[];
    listIdPerfilAutoatendimento: number[];
    listIdTipoImpressao: number[];
  }): void {
    const c = config.configPdv;
    this.idConfigPdv = c.idConfigPdv;

    this.form.patchValue({
      idEtapaKanbanPadraoVenda: c.idEtapaKanbanPadraoVenda,
      listIdTagVenda: config.listIdTagVenda || [],
      idListaPrecoPreferencial: c.idListaPrecoPreferencial,
      idNaturezaOperacaoPadrao: c.idNaturezaOperacaoPadrao,
      bloquearVendaSemEstoque: c.bloquearVendaSemEstoque,
      idLocalizacaoEstoquePadrao: c.idLocalizacaoEstoquePadrao,
      vendedorObrigatorio: c.vendedorObrigatorio,
      bloquearVendaValorInferior: c.bloquearVendaValorInferior,
      permitirVendaProdutoNaoCadastrado: c.permitirVendaProdutoNaoCadastrado,
      bloquearAlteracaoPreco: c.bloquearAlteracaoPreco,
      agrupamentoAutomatico: c.agrupamentoAutomatico,
      mostrarTelaRecebimento: c.mostrarTelaRecebimento,
      isEmissaoAutomaticaNFCe: c.isEmissaoAutomaticaNFCe,
      listIdTipoImpressao: config.listIdTipoImpressao || [],
      descontoMaximoItens: c.descontoMaximoItens,
      controlarCaixa: c.controlarCaixa,
      considerarRecebimentosFaturarPrevenda: c.considerarRecebimentosFaturarPrevenda,
      permitirCompartilhamentoCaixa: c.permitirCompartilhamentoCaixa,
      exibirValoresFormasRecebimento: c.exibirValoresFormasRecebimento,
      aberturaCaixaUltimoValor: c.aberturaCaixaUltimoValor,
      idTipoComposicaoCodigoBarras: c.idTipoComposicaoCodigoBarras,
      permitirAutoAtendimento: c.permitirAutoAtendimento,
      listIdPerfilAutoatendimento: config.listIdPerfilAutoatendimento || [],
    });

    this.descontoPerfil = config.listDescontoPerfil || [];

    // Aplica o estado inicial dos campos dependentes dos toggles mestre
    this.aplicarEstadoCaixa(c.controlarCaixa);
    this.aplicarEstadoAutoAtendimento(c.permitirAutoAtendimento);
  }

  public onDescontoPerfilChange(items: DescontoPerfilStruct[]): void {
    this.descontoPerfil = items;
  }

  public salvar(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios corretamente.', AlertType.error);
      return;
    }

    this.isSaving = true;
    const raw = this.form.getRawValue(); // inclui valores de campos desabilitados (preservados)

    const request: ConfigPdvRequest = {
      idEtapaKanbanPadraoVenda: raw.idEtapaKanbanPadraoVenda,
      idListaPrecoPreferencial: raw.idListaPrecoPreferencial,
      idNaturezaOperacaoPadrao: raw.idNaturezaOperacaoPadrao,
      bloquearVendaSemEstoque: raw.bloquearVendaSemEstoque,
      idLocalizacaoEstoquePadrao: raw.idLocalizacaoEstoquePadrao,
      vendedorObrigatorio: raw.vendedorObrigatorio,
      bloquearVendaValorInferior: raw.bloquearVendaValorInferior,
      permitirVendaProdutoNaoCadastrado: raw.permitirVendaProdutoNaoCadastrado,
      bloquearAlteracaoPreco: raw.bloquearAlteracaoPreco,
      agrupamentoAutomatico: raw.agrupamentoAutomatico,
      mostrarTelaRecebimento: raw.mostrarTelaRecebimento,
      isEmissaoAutomaticaNFCe: raw.isEmissaoAutomaticaNFCe,
      descontoMaximoItens: raw.descontoMaximoItens,
      controlarCaixa: raw.controlarCaixa,
      considerarRecebimentosFaturarPrevenda: raw.considerarRecebimentosFaturarPrevenda,
      permitirCompartilhamentoCaixa: raw.permitirCompartilhamentoCaixa,
      exibirValoresFormasRecebimento: raw.exibirValoresFormasRecebimento,
      aberturaCaixaUltimoValor: raw.aberturaCaixaUltimoValor,
      idTipoComposicaoCodigoBarras: raw.idTipoComposicaoCodigoBarras,
      permitirAutoAtendimento: raw.permitirAutoAtendimento,
      listIdTagVenda: raw.listIdTagVenda || [],
      listDescontoPerfil: this.descontoPerfil,
      listIdPerfilAutoatendimento: raw.listIdPerfilAutoatendimento || [],
      listIdTipoImpressao: raw.listIdTipoImpressao || [],
    };

    this.configPdvService.update(this.idConfigPdv, request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Configurações salvas com sucesso!', AlertType.success);
        this.voltar();
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Aviso', error || 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/vendas']);
  }
}
